import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import type { CurrentUser, User } from '@recruitee/account-types';
import { I18nService } from '@recruitee/i18n';
import { TasksActions } from '@recruitee/tasks-data';
import type { Task } from '@recruitee/tasks-types';
import { TasksAnalytics } from '@recruitee/tasks-util';
import { DialogService, DialogWidth, NotificationsController } from '@recruitee/user-interface';

import { TaskDialogComponent } from '../task-dialog/task-dialog.component';
import { TaskItemComponent } from '../task-item/task-item.component';

@Component({
  selector: 'rt-tasks-list',
  templateUrl: './tasks-list.component.html',
  styleUrls: ['./tasks-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TasksAnalytics],
  standalone: true,
  imports: [TaskItemComponent],
})
export class TasksListComponent {
  @Input() public tasks: Task[];
  @Input() public currentAdmin: CurrentUser;
  @Input() public editableCandidate: boolean = false;
  @Input() public allowedUsers: User[];
  @Input() public disallowedUsers: User[];

  @Output() public refreshList: EventEmitter<void> = new EventEmitter();

  public canViewCandidates = false;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private i18n: I18nService,
    private notification: NotificationsController,
    private viewContainerRef: ViewContainerRef,
    private tasksAnalytics: TasksAnalytics,
    private store: Store,
    private actions$: Actions,
  ) {}

  public ngOnInit(): void {
    this.checkAbility();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public taskTrackBy(index: number, task: Task): number {
    return task.id || index;
  }

  public onOpenTaskEditDialog(task: Task): void {
    if (!this.canViewCandidates) return;
    const data = {
      task: task,
      editableCandidate: this.editableCandidate,
      ...(this.allowedUsers ? { allowedUsers: this.allowedUsers } : {}),
      ...(this.disallowedUsers ? { disallowedUsers: this.disallowedUsers } : {}),
    };
    this.dialogService
      .custom(TaskDialogComponent, { width: DialogWidth.SmallX }, data, this.viewContainerRef)
      .afterClosed.pipe(filter(Boolean), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.refreshList.emit());
  }

  // eslint-disable-next-line max-lines-per-function
  public onTaskToggle({ task, completed }: { task: Task; completed: boolean }): void {
    this.store.dispatch(
      TasksActions.updateTask({
        payload: { data: { id: task.id, completed } },
      }),
    );

    this.actions$
      .pipe(ofType(TasksActions.updateTaskSuccess), take(1), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.refreshList.emit();

        const title = this.i18n.translate(
          completed ? 'app.dashboard.tasks.done.title' : 'app.dashboard.tasks.undone.title',
        );
        const message = this.i18n.translate(
          completed ? 'app.dashboard.tasks.done.message' : 'app.dashboard.tasks.undone.message',
        );

        const notificationRef = this.notification.success({
          title,
          message,
          topButtonLabel: this.i18n.translate('app.tasks.undo'),
        });

        if (completed) {
          this.tasksAnalytics.trackComplete(!!task.dueDate, task.adminIds?.length > 0);
        }

        notificationRef
          .topButtonClick()
          .pipe(
            take(1),
            tap(() => {
              this.store.dispatch(
                TasksActions.updateTask({
                  payload: { data: { id: task.id, completed: !completed } },
                }),
              );
            }),
            switchMap(() => this.actions$.pipe(ofType(TasksActions.updateTaskSuccess), take(1))),
            takeUntil(this.ngOnDestroy$.asObservable()),
          )
          .subscribe(() => {
            this.refreshList.emit();

            notificationRef.close();
          });
      });
  }

  private checkAbility(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.canViewCandidates = enabledMap[Ability.ViewCandidates];
      });
  }
}
